import { useCreateEvent } from "@/features/events/hooks/useCreateEvent"
import { EventType } from "@/features/events/types/EventType"
import { useEffect, useState } from "react"
import { ClickableItem, MealPlanData } from "../../../utils/api-objects/HealthPlan"
import { callUpdateMealPlanDataApi } from "../../../utils/apis/plan-service"
import { saveEvent } from "../../../utils/apis/user-service"
import { useAuthStore } from "../../../utils/stores/useAuthStore"
import { usePlansStore } from "../../../utils/stores/usePlansStore"
import { useProductsStore } from "../../../utils/stores/useProductsStore"
import { useUserStore } from "../../../utils/stores/useUserStore"

export const MealPlanDataBox: React.FC<{
    title: string,
    data: string[],
    source: string,
}> = ({
    title,
    data,
    source,
}) => {
        const { sendEvent } = useCreateEvent()
        const { userData } = useUserStore()
        const email = userData.email
        const [isExpanded, setIsExpanded] = useState(false)
        const { productsData } = useProductsStore()
        const membership = productsData.membership

        const handleTitle = () => {
            if (title.includes("Parametri")) {
                return "key_parameters";
            } else if (title.includes("Consigli alimentari")) {
                return "nutrition_recommendations"
            } else if (title.includes("Hero foods")) {
                return "top_hero_foods"
            } else if (title.includes("integratori")) {
                return "top_integratori"
            }
            return "";
        };

        const onShowMoreClick = async () => {
            setIsExpanded(!isExpanded)

            if (!email) return
            membership && saveEvent(
                email,
                isExpanded ? `show_less_meal_plan_${handleTitle()}_button_clicked` : `show_more_meal_plan_${handleTitle()}_button_clicked`,
                (success) => { console.log() },
                (error) => { console.log(error) },
                source,
            )

            await sendEvent({
                event: EventType.CLICK,
                sub_category: "meal_plan_interaction",
                event_metadata: { action: "show_more/less_on_card", title }
            })
        }

        // ********************************************************************************

        return (
            <div className={`self-start w-full border border-solid border-border rounded-lg shadow-platform-new px-[20px] py-[10px] flex flex-col space-y-[10px] bg-white`}>
                <div
                    className="font-bold text-[12px] leading-[33px]"
                >
                    {title}
                </div>
                <div
                    className={`relative overflow-hidden transition-all ${isExpanded ? "h-auto md:min-h-[70px] space-y-[10px]" : "max-h-[70px] md:h-[70px]"}`}
                >
                    {data.map((element, index) => (
                        <div key={index}>
                            <span
                                className={`font-normal text-[16px] leading-[24px] text-black`}
                            >
                                {isExpanded ? "• " : ""}{element}
                            </span>
                        </div>
                    ))}

                    {!isExpanded && (
                        <div className="absolute bottom-0 left-0 w-full h-[100px] bg-gradient-to-t from-white to-transparent pointer-events-none" />
                    )}
                </div>

                <div className="w-full">
                    <div
                        onClick={onShowMoreClick}
                        className="text-holifya-blue font-bold text-[12px]"
                    >
                        {isExpanded ? "Mostra meno" : "Mostra di più"}
                    </div>
                </div>
            </div>
        );
    }

export const MealPlanDataBoxWithCheckbox: React.FC<{
    title: string,
    data: ClickableItem[],
    fullData: MealPlanData,
    planId: string,
    source: string,
}> = ({
    title,
    data,
    fullData,
    planId,
    source,
}) => {
        const { sendEvent } = useCreateEvent()
        const { auth } = useAuthStore()
        const idToken = auth.idToken
        // const { userData } = useUserData()
        // const email = userData.email
        const { userData } = useUserStore()
        const email = userData.email
        const { plans, updatePlans } = usePlansStore()
        const mealPlans = plans.mealPlans
        const [isExpanded, setIsExpanded] = useState(false)
        const [checkedItems, setCheckedItems] = useState<boolean[]>(data.map(item => item.isChecked ?? false));
        const { productsData } = useProductsStore()
        const membership = productsData.membership

        const [progress, setProgress] = useState(0);

        useEffect(() => {
            const checkedCount = checkedItems.filter(item => item).length;
            const totalCount = checkedItems.length;
            setProgress(totalCount > 0 ? (checkedCount / totalCount) * 100 : 0);
        }, [checkedItems]);

        const handleTitle = () => {
            if (title.includes("Linee guida")) {
                return "guidelines";
            } else if (title.includes("Ricette")) {
                return "recepees"
            }
            return "";
        };

        const onShowMoreButtonClicked = async () => {
            setIsExpanded(!isExpanded)
            if (!email) return
            membership && saveEvent(
                email,
                isExpanded ? `show_less_${handleTitle()}_button_clicked` : `show_more_${handleTitle()}_button_clicked`,
                (success) => console.log(success),
                (error) => console.log(error),
            )

            await sendEvent({
                event: EventType.CLICK,
                sub_category: "meal_plan_interaction",
                event_metadata: { action: "show_more/less_on_card", title }
            })
        }

        const updateCheckboxStatus = async (index: number) => {
            try {
                const isChecked = !checkedItems[index];

                // Update the local state immediately
                const updatedCheckedItems = [...checkedItems];
                updatedCheckedItems[index] = isChecked;
                setCheckedItems(updatedCheckedItems);

                // Update the data array with the modified item
                const updatedData = data.map((item, i) =>
                    i === index ? { ...item, isChecked } : item // Update the changed item, keep the others as is
                );

                let key = ""
                switch (title) {
                    case "🍲 Linee guida alimentari principali":
                        key = "guidelines"
                        break
                    case "🍳 Ricette e accostamenti suggeriti":
                        key = "recepees"
                        break
                    case "🥙 Alimenti consigliati":
                        key = "food_tips"
                        break
                    case "💊 Integratori consigliati":
                        key = "integrators_suggested"
                        break
                    default:
                        console.error("Invalid title");
                        return;
                }

                // Update only the specific key in fullData
                const updatedFullData = {
                    ...fullData,
                    [key]: updatedData, // Replace only the modified field
                };


                // Call the API to update the meal plan
                const response = await callUpdateMealPlanDataApi(idToken!, planId, updatedFullData);

                const result = await response.json();

                if (result.updated_plan && mealPlans) {
                    // Replace the updated meal plan in the list
                    const updatedMealPlans = mealPlans.map((mp) =>
                        mp.id === result.updated_plan.id ? result.updated_plan : mp
                    );

                    // Update state with the new meal plans
                    updatePlans({ mealPlans: updatedMealPlans });

                    if (!email) return
                    membership && saveEvent(
                        email,
                        `${isChecked ? "toggle" : "untoggle"}_meal_plan_on_${key}_${data[index].text}`,
                        () => { },
                        () => { },
                    )
                    await sendEvent({
                        event: EventType.CLICK,
                        sub_category: "meal_plan_interaction",
                        event_metadata: { action: "show_more/less_on_card", title }
                    })
                } else {
                    console.error("Failed to get updated plan from API");
                }

                if (!result.success) {
                    console.error("Failed to update checkbox status");

                    // Optionally revert the change if the update fails
                    setCheckedItems(prev => {
                        const newCheckedItems = [...prev];
                        newCheckedItems[index] = !newCheckedItems[index]; // Revert the change
                        return newCheckedItems;
                    });
                }
            } catch (error) {
                console.error("Error updating checkbox status:", error);
            }
        };

        const handleCheckboxChange = (index: number) => {
            // const a  = !data[index].isChecked
            // console.log(a)
            // console.log(data)
            // setCheckedItems((prev) => {
            //     const newCheckedItems = [...prev];
            //     newCheckedItems[index] = !newCheckedItems[index];
            //     return newCheckedItems;
            // });

            updateCheckboxStatus(index);
        };

        const handleSecondText = () => {
            switch (title) {
                case "🍲 Linee guida alimentari principali":
                    return "Stai seguendo queste linee guida?"
                case "🍳 Ricette e accostamenti suggeriti":
                    return "Hai già integrato questi suggerimenti?"
                case "🥙  Alimenti consigliati":
                    return "Fanno parte della tua alimentazione quotidiana?"
                case "💊  Integratori consigliati":
                    return "Li hai già inseriti nella tua integrazione?"
                default:
                    return ""
            }
        }

        // ********************************************************************************

        return (
            <div className={`self-start w-full border border-solid border-border rounded-lg shadow-platform-new px-[20px] py-[10px] flex flex-col space-y-[10px] bg-white`}>
                <div>
                    <div className="flex items-center justify-between">
                        <div
                            className="font-bold text-[12px] leading-[33px]"
                        >
                            {title}
                        </div>
                        <div className="relative w-[20px] h-[20px] flex items-center justify-center">
                            {/* Background Ring (Grey) */}
                            <svg className="absolute top-0 left-0 w-full h-full" viewBox="0 0 36 36">
                                <circle
                                    cx="18"  // Adjusted center point
                                    cy="18"
                                    r="16"  // Keeps 18px radius
                                    stroke="#F2F2F2"
                                    strokeWidth="4"  // Keeps 4px stroke width
                                    fill="none"
                                />
                            </svg>

                            {/* Progress Ring */}
                            <svg className="absolute top-0 left-0 w-full h-full rotate-[-90deg]" viewBox="0 0 36 36">
                                <circle
                                    cx="18"  // Adjusted center point
                                    cy="18"
                                    r="16"
                                    stroke="#3BB123"
                                    strokeWidth="4"
                                    strokeDasharray="100, 100"
                                    strokeDashoffset={`${100 - progress}`}
                                    strokeLinecap="round"
                                    fill={progress === 100 ? "#9AED89" : "none"}
                                    style={{ transition: "stroke-dashoffset 0.5s ease-in-out" }}
                                />
                            </svg>

                            {progress === 100 && (
                                <span className="absolute w-[8px] text-[8px] leading-none">
                                    🎉
                                </span>
                            )}
                        </div>
                    </div>
                    {
                        isExpanded && <div className="font-normal text-[12px] leading-[30px]">
                            {handleSecondText()}
                        </div>
                    }
                </div>


                <div
                    className={`relative overflow-hidden transition-all ${isExpanded ? "h-auto md:min-h-[40px] space-y-[10px]" : "max-h-[70px] md:h-[70px]"}`}
                >
                    {data.map((item, index) => (
                        <div key={index} className="flex items-center space-x-2">
                            {isExpanded && (
                                <label className="relative flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={checkedItems[index]}
                                        onChange={() => handleCheckboxChange(index)}
                                        className="w-[18px] h-[18px] cursor-pointer 
                                           appearance-none border-2 border-black 
                                           rounded-[4px] flex items-center justify-center 
                                           checked:bg-[#3BB123] checked:border-[#3BB123] 
                                           relative"
                                    />
                                    {/* White checkmark when checked */}
                                    {checkedItems[index] && (
                                        <svg
                                            className="absolute left-[3px] top-[3px] w-[12px] h-[12px] text-white"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth={4}
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                </label>
                            )}
                            <span
                                className={`text-[16px] leading-[24px] ${checkedItems[index] ? "text-[#3BB123] font-bold" : "text-black font-normal"}`}
                            >
                                {item.text}
                            </span>
                        </div>
                    ))}

                    {!isExpanded && (
                        <div className="absolute bottom-0 left-0 w-full h-[100px] bg-gradient-to-t from-white to-transparent pointer-events-none" />
                    )}
                </div>

                <div className="w-full">
                    <div
                        onClick={onShowMoreButtonClicked}
                        className="text-holifya-blue font-bold text-[12px]"
                    >
                        {isExpanded ? "Mostra meno" : "Mostra di più"}
                    </div>
                </div>
            </div>
        );
    }
